import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  Archive,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  RefreshCw,
  Clock,
  Database,
  Shield,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const TABLE_OPTIONS = [
  { value: 'user_profiles', label: 'Profils Utilisateurs' },
  { value: 'properties', label: 'Propriétés' },
  { value: 'appointments', label: 'Rendez-vous' },
  { value: 'property_inquiries', label: 'Demandes de Propriétés' },
  { value: 'activity_logs', label: 'Journaux d\'Activité' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'login_history', label: 'Historique de Connexion' },
  { value: 'login_sessions', label: 'Sessions de Connexion' },
  { value: 'user_journey_events', label: 'Événements Parcours Utilisateur' },
  { value: 'revolution_leads', label: 'Leads Révolution' },
  { value: 'professional_applications', label: 'Candidatures Professionnelles' },
  { value: 'professional_bookings', label: 'Réservations Professionnelles' },
  { value: 'messages', label: 'Messages' },
  { value: 'escalation_logs', label: 'Journaux d\'Escalade' },
];

const RETENTION_UNITS = [
  { value: 'days', label: 'Jours' },
  { value: 'months', label: 'Mois' },
  { value: 'years', label: 'Années' },
];

const ACTION_OPTIONS = [
  { value: 'archive', label: 'Archiver', color: 'bg-amber-100 text-amber-800' },
  { value: 'anonymize', label: 'Anonymiser', color: 'bg-blue-100 text-blue-800' },
  { value: 'delete', label: 'Supprimer', color: 'bg-red-100 text-red-800' },
];

const EMPTY_FORM = {
  table_name: 'user_profiles',
  retention_value: 24,
  retention_unit: 'months',
  action_on_expiry: 'archive',
  legal_basis: '',
  is_active: true,
  notes: '',
};

const DataArchivalPolicyManager = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expandedId, setExpandedId] = useState(null);
  const [runningPolicy, setRunningPolicy] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        ?.from('data_archival_policies')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setPolicies(data || []);
    } catch (err) {
      setError('Erreur lors du chargement des politiques: ' + err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = {
        table_name: form?.table_name,
        retention_value: parseInt(form?.retention_value, 10),
        retention_unit: form?.retention_unit,
        action_on_expiry: form?.action_on_expiry,
        legal_basis: form?.legal_basis,
        is_active: form?.is_active,
        notes: form?.notes,
        updated_at: new Date()?.toISOString(),
      };

      if (editingId) {
        const { error: updateError } = await supabase
          ?.from('data_archival_policies')
          ?.update(payload)
          ?.eq('id', editingId);
        if (updateError) throw updateError;
        setSuccess('Politique mise à jour avec succès.');
      } else {
        const { error: insertError } = await supabase
          ?.from('data_archival_policies')
          ?.insert({ ...payload, created_at: new Date()?.toISOString() });
        if (insertError) throw insertError;
        setSuccess('Politique créée avec succès.');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchPolicies();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement: ' + err?.message);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 4000);
    }
  };

  const handleEdit = (policy) => {
    setForm({
      table_name: policy?.table_name || 'user_profiles',
      retention_value: policy?.retention_value || 24,
      retention_unit: policy?.retention_unit || 'months',
      action_on_expiry: policy?.action_on_expiry || 'archive',
      legal_basis: policy?.legal_basis || '',
      is_active: policy?.is_active ?? true,
      notes: policy?.notes || '',
    });
    setEditingId(policy?.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette politique d\'archivage ?')) return;
    try {
      const { error: delError } = await supabase
        ?.from('data_archival_policies')
        ?.delete()
        ?.eq('id', id);
      if (delError) throw delError;
      setSuccess('Politique supprimée.');
      fetchPolicies();
    } catch (err) {
      setError('Erreur lors de la suppression: ' + err?.message);
    } finally {
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleToggleActive = async (policy) => {
    try {
      const { error: toggleError } = await supabase
        ?.from('data_archival_policies')
        ?.update({ is_active: !policy?.is_active, updated_at: new Date()?.toISOString() })
        ?.eq('id', policy?.id);
      if (toggleError) throw toggleError;
      fetchPolicies();
    } catch (err) {
      setError('Erreur lors de la mise à jour: ' + err?.message);
    }
  };

  const handleRunNow = async (policy) => {
    setRunningPolicy(policy?.id);
    try {
      const { error: logError } = await supabase
        ?.from('archival_execution_logs')
        ?.insert({
          policy_id: policy?.id,
          table_name: policy?.table_name,
          action_taken: policy?.action_on_expiry,
          status: 'manual_trigger',
          triggered_by: 'admin',
          executed_at: new Date()?.toISOString(),
          notes: 'Exécution manuelle déclenchée par l\'administrateur',
        });
      if (logError) throw logError;
      setSuccess(`Politique "${TABLE_OPTIONS?.find(t => t?.value === policy?.table_name)?.label}" déclenchée manuellement.`);
    } catch (err) {
      setError('Erreur lors du déclenchement: ' + err?.message);
    } finally {
      setRunningPolicy(null);
      setTimeout(() => setSuccess(null), 4000);
    }
  };

  const getActionBadge = (action) => {
    const opt = ACTION_OPTIONS?.find(a => a?.value === action);
    return opt ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${opt?.color}`}>
        {opt?.label}
      </span>
    ) : null;
  };

  const getTableLabel = (val) =>
    TABLE_OPTIONS?.find(t => t?.value === val)?.label || val;

  const formatRetention = (val, unit) => {
    const u = RETENTION_UNITS?.find(r => r?.value === unit)?.label || unit;
    return `${val} ${u}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Archive className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Politiques d'Archivage</h3>
            <p className="text-sm text-gray-500">Gérez la rétention et l'archivage des données par table</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPolicies}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle Politique
          </button>
        </div>
      </div>
      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}
      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h4 className="font-semibold text-gray-900">
              {editingId ? 'Modifier la Politique' : 'Nouvelle Politique d\'Archivage'}
            </h4>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Table de Données *</label>
              <select
                value={form?.table_name}
                onChange={e => setForm(f => ({ ...f, table_name: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              >
                {TABLE_OPTIONS?.map(opt => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action à l'Expiration *</label>
              <select
                value={form?.action_on_expiry}
                onChange={e => setForm(f => ({ ...f, action_on_expiry: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              >
                {ACTION_OPTIONS?.map(opt => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durée de Rétention *</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  value={form?.retention_value}
                  onChange={e => setForm(f => ({ ...f, retention_value: e?.target?.value }))}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                />
                <select
                  value={form?.retention_unit}
                  onChange={e => setForm(f => ({ ...f, retention_unit: e?.target?.value }))}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {RETENTION_UNITS?.map(u => (
                    <option key={u?.value} value={u?.value}>{u?.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Base Légale (RGPD/Ivoirien)</label>
              <input
                type="text"
                value={form?.legal_basis}
                onChange={e => setForm(f => ({ ...f, legal_basis: e?.target?.value }))}
                placeholder="ex: Art. 6(1)(c) RGPD, Loi n°2013-450"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={form?.notes}
                onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
                rows={2}
                placeholder="Justification ou contexte de cette politique..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Politique Active</label>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, is_active: !f?.is_active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form?.is_active ? 'bg-amber-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form?.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Policies List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-40" />
                  <div className="h-3 bg-gray-100 rounded w-56" />
                </div>
                <div className="h-8 bg-gray-200 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : policies?.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
          <Archive className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucune politique d'archivage définie</p>
          <p className="text-gray-400 text-sm mt-1">Créez votre première politique pour gérer la rétention des données.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {policies?.map(policy => (
            <div key={policy?.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-2 h-10 rounded-full flex-shrink-0 ${policy?.is_active ? 'bg-amber-400' : 'bg-gray-300'}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{getTableLabel(policy?.table_name)}</span>
                      {getActionBadge(policy?.action_on_expiry)}
                      {!policy?.is_active && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Inactif</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Rétention: {formatRetention(policy?.retention_value, policy?.retention_unit)}
                      </span>
                      {policy?.legal_basis && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {policy?.legal_basis}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleActive(policy)}
                    className={`p-1.5 rounded-lg transition-colors ${policy?.is_active ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-50'}`}
                    title={policy?.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {policy?.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleRunNow(policy)}
                    disabled={runningPolicy === policy?.id}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    {runningPolicy === policy?.id ? 'En cours...' : 'Exécuter'}
                  </button>
                  <button onClick={() => handleEdit(policy)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(policy?.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setExpandedId(expandedId === policy?.id ? null : policy?.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {expandedId === policy?.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {expandedId === policy?.id && policy?.notes && (
                <div className="px-5 pb-4 border-t border-gray-100 pt-3">
                  <p className="text-sm text-gray-600 flex items-start gap-2">
                    <Database className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    {policy?.notes}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataArchivalPolicyManager;
